import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import styles from './FlightSearchForm.module.scss';
import { SVGPlane, SVGCalendar, SVGUsers, SVGChevronDown, SVGSearch } from '../assets/PackageSVG';
import { searchAirports, getAirportByCode, type Airport } from '../data/airports';
import { DateRangePickerWidget } from './DateRangePickerWidget';
import { useOrigin } from '../context/OriginContext';

export type TripType = 'one_way' | 'round_trip';

export type FlightSearchCriteria = {
    tripType: TripType;
    origin: string;
    destination: string;
    /** YYYY-MM-DD */
    departDate: string;
    /** YYYY-MM-DD — only present for a round trip. */
    returnDate?: string;
    travelers: {
        adults: number;
        children: { age: number }[];
    };
};

type FlightSearchFormProps = {
    /** Collected, validated search criteria — sending it to the backend is
     * the caller's job (there's no dedicated flight-search gateway call to
     * hook into yet). */
    onSearch: (criteria: FlightSearchCriteria) => void;
    defaultOrigin?: string;
    defaultDestination?: string;
    /** Selections to show on mount -- a search that was already submitted,
     * restored when a saved chat is reopened. Only read once; later
     * changes to it don't reset what the user is editing. */
    initialCriteria?: FlightSearchCriteria;
};

type ActiveField = 'origin' | 'destination' | 'dates' | 'travelers' | null;

const MAX_ADULTS = 9;
const MAX_CHILDREN = 8;

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (iso: string, days: number) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

const airportLabel = (airport: Airport) => `${airport.city} (${airport.code})`;

/**
 * Portals its child panel to document.body, positioned to hang below the
 * given anchor via `position: fixed`. FlightSearchForm renders inside the
 * chat thread's scrolling message list — a plain `position: absolute`
 * popover gets clipped the moment the anchor is near that container's own
 * edge, since absolute positioning still respects an ancestor's overflow
 * clipping. `fixed` + a portal escapes both the clipping and the stacking
 * context, the same trick Modal.tsx uses for the booking flow.
 */
function FloatingPanel({
    anchorRef,
    align,
    matchWidth,
    children,
}: {
    anchorRef: React.RefObject<HTMLElement>;
    align: 'left' | 'right';
    /** Panel width matches the anchor's width (used for the airport
     * dropdowns, which should line up with their input field). */
    matchWidth?: boolean;
    children: React.ReactElement;
}) {
    const [style, setStyle] = useState<React.CSSProperties | null>(null);

    useLayoutEffect(() => {
        const update = () => {
            const el = anchorRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const next: React.CSSProperties = { position: 'fixed', top: rect.bottom + 8 };
            if (align === 'left') next.left = rect.left;
            else next.right = window.innerWidth - rect.right;
            if (matchWidth) next.width = rect.width;
            setStyle(next);
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [anchorRef, align, matchWidth]);

    if (!style) return null;
    return createPortal(
        React.cloneElement(children, {
            style: { ...style, ...(children.props.style || {}) },
            'data-floating-panel': true,
        }),
        document.body
    );
}

function SwapIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m17 3 4 4-4 4" />
            <path d="M3 7h18" />
            <path d="m7 21-4-4 4-4" />
            <path d="M21 17H3" />
        </svg>
    );
}

/**
 * Origin/destination input — type-ahead over the shared airport list, shown
 * as "City, Country" with the full airport name and code underneath (the
 * layout most travel sites converge on: code alone isn't recognizable to
 * most travellers, city alone isn't specific enough at multi-airport
 * cities like London or Tokyo).
 */
function AirportField({
    id,
    label,
    placeholder,
    query,
    onQueryChange,
    selected,
    onSelect,
    isOpen,
    onOpen,
    onClose,
}: {
    id: string;
    label: string;
    placeholder: string;
    query: string;
    onQueryChange: (value: string) => void;
    selected: Airport | null;
    onSelect: (airport: Airport) => void;
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
}) {
    const [highlight, setHighlight] = useState(0);
    const results = useMemo(() => searchAirports(query, 8), [query]);
    const wrapRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setHighlight(0);
    }, [query, isOpen]);

    return (
        <div className={styles.fieldWrap} ref={wrapRef}>
            <label className={styles.fieldLabel} htmlFor={id}>{label}</label>
            <div className={styles.airportInputWrap}>
                <SVGPlane className={styles.fieldIcon} aria-hidden="true" />
                <input
                    id={id}
                    className={styles.airportInput}
                    type="text"
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={`${id}-listbox`}
                    placeholder={placeholder}
                    value={query}
                    onFocus={onOpen}
                    onChange={(e) => { onQueryChange(e.target.value); onOpen(); }}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            onOpen();
                            setHighlight((i) => Math.min(i + 1, results.length - 1));
                        } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setHighlight((i) => Math.max(i - 1, 0));
                        } else if (e.key === 'Enter') {
                            e.preventDefault();
                            const pick = results[highlight];
                            if (isOpen && pick) onSelect(pick);
                        } else if (e.key === 'Escape') {
                            onClose();
                        }
                    }}
                />
            </div>

            {isOpen && (
                <FloatingPanel anchorRef={wrapRef} align="left" matchWidth>
                    <ul id={`${id}-listbox`} role="listbox" className={styles.airportDropdown}>
                        {results.length === 0 && (
                            <li className={styles.airportEmpty}>No airports match "{query}"</li>
                        )}
                        {results.map((airport, idx) => (
                            <li
                                key={airport.code}
                                role="option"
                                aria-selected={selected?.code === airport.code}
                                className={`${styles.airportOption} ${idx === highlight ? styles.airportOptionActive : ''}`}
                                onMouseEnter={() => setHighlight(idx)}
                                onMouseDown={(e) => { e.preventDefault(); onSelect(airport); }}
                            >
                                <span className={styles.airportCode}>{airport.code}</span>
                                <span className={styles.airportInfo}>
                                    <span className={styles.airportCity}>
                                        {airport.city}, {airport.country}
                                    </span>
                                    <span className={styles.airportName}>{airport.name}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </FloatingPanel>
            )}
        </div>
    );
}

function StepRow({
    label,
    sublabel,
    value,
    min,
    max,
    onChange,
}: {
    label: string;
    sublabel?: string;
    value: number;
    min: number;
    max: number;
    onChange: (next: number) => void;
}) {
    return (
        <div className={styles.stepRow}>
            <span className={styles.stepLabelGroup}>
                <span className={styles.stepLabel}>{label}</span>
                {sublabel && <span className={styles.stepSublabel}>{sublabel}</span>}
            </span>
            <div className={styles.stepControls}>
                <button
                    type="button"
                    className={styles.stepButton}
                    disabled={value <= min}
                    onClick={() => onChange(Math.max(min, value - 1))}
                    aria-label={`decrease ${label}`}
                >
                    −
                </button>
                <span className={styles.stepValue}>{value}</span>
                <button
                    type="button"
                    className={styles.stepButton}
                    disabled={value >= max}
                    onClick={() => onChange(Math.min(max, value + 1))}
                    aria-label={`increase ${label}`}
                >
                    +
                </button>
            </div>
        </div>
    );
}

export function FlightSearchForm({ onSearch, defaultOrigin, defaultDestination, initialCriteria }: FlightSearchFormProps) {
    const { origin: detectedOrigin } = useOrigin();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const travelersBtnRef = useRef<HTMLButtonElement | null>(null);
    const datesFieldRef = useRef<HTMLDivElement | null>(null);

    const [tripType, setTripType] = useState<TripType>(initialCriteria?.tripType ?? 'round_trip');

    const initialOrigin = initialCriteria ? getAirportByCode(initialCriteria.origin) ?? null : null;
    const initialDestination = initialCriteria ? getAirportByCode(initialCriteria.destination) ?? null : null;
    const [origin, setOriginAirport] = useState<Airport | null>(initialOrigin);
    const [originQuery, setOriginQuery] = useState(initialOrigin ? airportLabel(initialOrigin) : '');
    const [destination, setDestinationAirport] = useState<Airport | null>(initialDestination);
    const [destinationQuery, setDestinationQuery] = useState(initialDestination ? airportLabel(initialDestination) : '');

    const [departDate, setDepartDate] = useState(initialCriteria?.departDate ?? '');
    const [returnDate, setReturnDate] = useState(initialCriteria?.returnDate ?? '');

    const [adults, setAdults] = useState(initialCriteria?.travelers.adults ?? 1);
    const [children, setChildren] = useState<{ age: number }[]>(initialCriteria?.travelers.children ?? []);

    const [activeField, setActiveField] = useState<ActiveField>(null);

    /* Pre-fill "From" once — the explicit prop wins, then whatever
       OriginContext already detected (browser geolocation / IP lookup),
       matching how most flight search boxes default the origin field. */
    useEffect(() => {
        if (origin) return;
        const code = defaultOrigin || detectedOrigin?.airport;
        if (!code) return;
        const airport = getAirportByCode(code);
        if (airport) {
            setOriginAirport(airport);
            setOriginQuery(airportLabel(airport));
        }
    }, [origin, detectedOrigin?.airport, defaultOrigin]);

    useEffect(() => {
        if (destination || !defaultDestination) return;
        const airport = getAirportByCode(defaultDestination);
        if (airport) {
            setDestinationAirport(airport);
            setDestinationQuery(airportLabel(airport));
        }
    }, [defaultDestination, destination]);

    useEffect(() => {
        const onDocMouseDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Floating panels (dates/travelers/airport dropdown) are
            // portaled to document.body, so they fall outside rootRef's
            // own DOM subtree even while open — a click inside one must
            // not count as "outside" and close it.
            if (rootRef.current?.contains(target)) return;
            if (target.closest?.('[data-floating-panel]')) return;
            setActiveField(null);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActiveField(null);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    const selectOrigin = (airport: Airport) => {
        setOriginAirport(airport);
        setOriginQuery(airportLabel(airport));
        setActiveField(null);
    };
    const selectDestination = (airport: Airport) => {
        setDestinationAirport(airport);
        setDestinationQuery(airportLabel(airport));
        setActiveField(null);
    };

    const swap = () => {
        const nextOrigin = destination;
        const nextDestination = origin;
        setOriginAirport(nextOrigin);
        setOriginQuery(nextOrigin ? airportLabel(nextOrigin) : '');
        setDestinationAirport(nextDestination);
        setDestinationQuery(nextDestination ? airportLabel(nextDestination) : '');
    };

    const setChildCount = (count: number) => {
        setChildren((current) => Array.from({ length: count }, (_, i) => current[i] ?? { age: 8 }));
    };

    const minDate = todayISO();
    const maxDate = addDaysISO(minDate, 365);

    const dateLabel = useMemo(() => {
        if (!departDate) return 'Select dates';
        const depart = format(new Date(`${departDate}T00:00:00`), 'MMM d');
        if (tripType === 'one_way') return `Depart ${depart}`;
        if (!returnDate) return `${depart} – Select return`;
        const ret = format(new Date(`${returnDate}T00:00:00`), 'MMM d');
        return `${depart} – ${ret}`;
    }, [departDate, returnDate, tripType]);

    const travelersLabel = useMemo(() => {
        const parts = [`${adults} adult${adults > 1 ? 's' : ''}`];
        if (children.length > 0) parts.push(`${children.length} child${children.length > 1 ? 'ren' : ''}`);
        return parts.join(', ');
    }, [adults, children.length]);

    const sameAirport = Boolean(origin && destination && origin.code === destination.code);
    const canSearch = Boolean(
        origin && destination && !sameAirport && departDate && (tripType === 'one_way' || returnDate)
    );

    const handleSearch = () => {
        if (!canSearch || !origin || !destination) return;
        onSearch({
            tripType,
            origin: origin.code,
            destination: destination.code,
            departDate,
            returnDate: tripType === 'round_trip' ? returnDate : undefined,
            travelers: { adults, children },
        });
    };

    return (
        <div ref={rootRef} className={styles.root}>
            <div className={styles.topRow}>
                <div className={styles.tripTypeToggle} role="radiogroup" aria-label="Trip type">
                    <button
                        type="button"
                        role="radio"
                        aria-checked={tripType === 'round_trip'}
                        className={`${styles.tripTypeBtn} ${tripType === 'round_trip' ? styles.tripTypeBtnActive : ''}`}
                        onClick={() => setTripType('round_trip')}
                    >
                        Round trip
                    </button>
                    <button
                        type="button"
                        role="radio"
                        aria-checked={tripType === 'one_way'}
                        className={`${styles.tripTypeBtn} ${tripType === 'one_way' ? styles.tripTypeBtnActive : ''}`}
                        onClick={() => { setTripType('one_way'); setReturnDate(''); }}
                    >
                        One way
                    </button>
                </div>

                <div className={styles.popoverAnchor}>
                    <button
                        ref={travelersBtnRef}
                        type="button"
                        className={styles.pillButton}
                        onClick={() => setActiveField((f) => (f === 'travelers' ? null : 'travelers'))}
                    >
                        <SVGUsers className={styles.pillIcon} aria-hidden="true" />
                        {travelersLabel}
                        <SVGChevronDown className={styles.pillChevron} aria-hidden="true" />
                    </button>

                    {activeField === 'travelers' && (
                        <FloatingPanel anchorRef={travelersBtnRef} align="right">
                            <div className={styles.popoverPanel}>
                                <StepRow label="Adults" value={adults} min={1} max={MAX_ADULTS} onChange={setAdults} />
                                <StepRow label="Children" sublabel="Ages 0–17" value={children.length} min={0} max={MAX_CHILDREN} onChange={setChildCount} />
                                {children.map((child, index) => (
                                    <div key={index} className={styles.childAgeRow}>
                                        <span className={styles.childAgeLabel}>Child {index + 1} age</span>
                                        <select
                                            className={styles.childAgeSelect}
                                            value={child.age}
                                            onChange={(e) =>
                                                setChildren((current) =>
                                                    current.map((c, i) => (i === index ? { age: Number(e.target.value) } : c))
                                                )
                                            }
                                        >
                                            {Array.from({ length: 18 }, (_, age) => (
                                                <option key={age} value={age}>{age === 0 ? '< 1 year' : `${age} year`}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                                <button type="button" className={styles.popoverDoneBtn} onClick={() => setActiveField(null)}>
                                    Done
                                </button>
                            </div>
                        </FloatingPanel>
                    )}
                </div>
            </div>

            <div className={styles.fieldsRow}>
                <div className={styles.airportPair}>
                    <AirportField
                        id="flight-origin"
                        label="From"
                        placeholder="Where from?"
                        query={originQuery}
                        onQueryChange={(v) => { setOriginQuery(v); setOriginAirport(null); }}
                        selected={origin}
                        onSelect={selectOrigin}
                        isOpen={activeField === 'origin'}
                        onOpen={() => setActiveField('origin')}
                        onClose={() => setActiveField(null)}
                    />
                    <button type="button" className={styles.swapButton} onClick={swap} aria-label="Swap origin and destination">
                        <SwapIcon className={styles.swapIcon} />
                    </button>
                    <AirportField
                        id="flight-destination"
                        label="To"
                        placeholder="Where to?"
                        query={destinationQuery}
                        onQueryChange={(v) => { setDestinationQuery(v); setDestinationAirport(null); }}
                        selected={destination}
                        onSelect={selectDestination}
                        isOpen={activeField === 'destination'}
                        onOpen={() => setActiveField('destination')}
                        onClose={() => setActiveField(null)}
                    />
                </div>

                <div className={styles.popoverAnchor}>
                    <div className={styles.fieldWrap} ref={datesFieldRef}>
                        <span className={styles.fieldLabel}>Dates</span>
                        <button
                            type="button"
                            className={styles.dateButton}
                            onClick={() => setActiveField((f) => (f === 'dates' ? null : 'dates'))}
                        >
                            <SVGCalendar className={styles.fieldIcon} aria-hidden="true" />
                            {dateLabel}
                        </button>
                    </div>

                    {activeField === 'dates' && tripType === 'round_trip' && (
                        <FloatingPanel anchorRef={datesFieldRef} align="left">
                            <div className={`${styles.popoverPanel} ${styles.datePopoverPanel}`}>
                                <DateRangePickerWidget
                                    payload={{
                                        min_date: minDate,
                                        max_date: maxDate,
                                        default_from: departDate || minDate,
                                        default_to: returnDate || addDaysISO(departDate || minDate, 7),
                                        submitted_value: departDate && returnDate
                                            ? { from: departDate, to: returnDate, nights: 0 }
                                            : undefined,
                                    }}
                                    onSubmit={(value) => {
                                        setDepartDate(value.from);
                                        setReturnDate(value.to);
                                        setActiveField(null);
                                    }}
                                />
                            </div>
                        </FloatingPanel>
                    )}

                    {activeField === 'dates' && tripType === 'one_way' && (
                        <FloatingPanel anchorRef={datesFieldRef} align="left">
                            <div className={`${styles.popoverPanel} ${styles.singleDatePanel}`}>
                                <label className={styles.singleDateLabel} htmlFor="flight-depart-date">Depart</label>
                                <input
                                    id="flight-depart-date"
                                    type="date"
                                    className={styles.singleDateInput}
                                    min={minDate}
                                    max={maxDate}
                                    value={departDate || minDate}
                                    onChange={(e) => setDepartDate(e.target.value)}
                                />
                                <button type="button" className={styles.popoverDoneBtn} onClick={() => setActiveField(null)}>
                                    Done
                                </button>
                            </div>
                        </FloatingPanel>
                    )}
                </div>

                <button type="button" className={styles.searchButton} disabled={!canSearch} onClick={handleSearch}>
                    <SVGSearch className={styles.searchIcon} aria-hidden="true" />
                    Search flights
                </button>
            </div>

            {sameAirport && (
                <p className={styles.inlineWarning}>Origin and destination can't be the same airport.</p>
            )}
        </div>
    );
}
